import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  increment,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { auth, db, storage } from './firebase';
import { firestoreSnapshotListenError } from './firestoreListenDebug';
import type {
  JobApplication,
  JobApplicationResume,
  JobApplicationStatus,
  JobEmploymentType,
  JobPosting,
  JobPostingStatus,
} from '../types/hiring';
import type { PayrollDepartment } from '../types/finances';

export const JOB_POSTINGS = 'jobPostings';
export const JOB_APPLICATIONS = 'jobApplications';
export const JOB_APPLICATIONS_STORAGE_PREFIX = 'job-applications';
export const MAX_RESUME_BYTES = 25 * 1024 * 1024;

const ALLOWED_RESUME_EXTENSIONS = ['.pdf', '.doc', '.docx'];

export function sanitizeResumeFileName(name: string): string {
  const base = (name || 'resume.pdf').replace(/[^\w.\-]+/g, '_').slice(0, 120);
  const lower = base.toLowerCase();
  if (ALLOWED_RESUME_EXTENSIONS.some(ext => lower.endsWith(ext))) return base;
  return `${base.replace(/\.[^.]+$/, '') || 'resume'}.pdf`;
}

export async function getJobApplicationResumeUrl(storagePath: string): Promise<string> {
  return getDownloadURL(ref(storage, storagePath));
}

export function isAllowedResumeFile(file: File): boolean {
  const fileName = sanitizeResumeFileName(file.name);
  const ct = (file.type || '').toLowerCase().split(';')[0].trim();
  const lower = fileName.toLowerCase();
  if (file.size <= 0 || file.size > MAX_RESUME_BYTES) return false;
  if (ct === 'application/pdf' && lower.endsWith('.pdf')) return true;
  if (ct === 'application/msword' && lower.endsWith('.doc')) return true;
  if (
    ct === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    && lower.endsWith('.docx')
  ) {
    return true;
  }
  if ((ct === 'application/octet-stream' || !ct) && ALLOWED_RESUME_EXTENSIONS.some(e => lower.endsWith(e))) {
    return true;
  }
  return ALLOWED_RESUME_EXTENSIONS.some(e => lower.endsWith(e));
}

function parseDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    return (value as { toDate: () => Date }).toDate();
  }
  const d = new Date(value as string);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function slugifyJobTitle(title: string): string {
  const base = title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  return base || 'position';
}

export async function ensureUniqueJobSlug(baseSlug: string, excludeId?: string): Promise<string> {
  let candidate = baseSlug;
  let n = 0;
  while (n < 50) {
    const q = query(collection(db, JOB_POSTINGS), where('slug', '==', candidate));
    const snap = await getDocs(q);
    const conflict = snap.docs.find(d => d.id !== excludeId);
    if (!conflict) return candidate;
    n += 1;
    candidate = `${baseSlug}-${n}`;
  }
  return `${baseSlug}-${Date.now()}`;
}

function parseJobPosting(id: string, raw: Record<string, unknown>): JobPosting {
  return {
    id,
    slug: String(raw.slug || ''),
    title: String(raw.title || ''),
    department: String(raw.department || 'other') as PayrollDepartment | string,
    location: String(raw.location || ''),
    employmentType: (raw.employmentType as JobEmploymentType) || 'full-time',
    description: String(raw.description || ''),
    requirements: String(raw.requirements || ''),
    status: (raw.status as JobPostingStatus) || 'draft',
    payrollPositionId:
      typeof raw.payrollPositionId === 'string' ? raw.payrollPositionId : null,
    publishedAt: parseDate(raw.publishedAt),
    closedAt: parseDate(raw.closedAt),
    applicationCount: typeof raw.applicationCount === 'number' ? raw.applicationCount : 0,
    createdAt: parseDate(raw.createdAt) || new Date(),
    updatedAt: parseDate(raw.updatedAt) || new Date(),
    createdBy: String(raw.createdBy || ''),
  };
}

function parseResume(raw: unknown): JobApplicationResume {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  return {
    fileName: String(r.fileName || 'resume.pdf'),
    storagePath: String(r.storagePath || ''),
    contentType: String(r.contentType || 'application/pdf'),
    sizeBytes: typeof r.sizeBytes === 'number' ? r.sizeBytes : 0,
  };
}

function parseJobApplication(id: string, raw: Record<string, unknown>): JobApplication {
  return {
    id,
    jobPostingId: String(raw.jobPostingId || ''),
    jobSlug: String(raw.jobSlug || ''),
    jobTitle: String(raw.jobTitle || ''),
    firstName: String(raw.firstName || ''),
    lastName: String(raw.lastName || ''),
    email: String(raw.email || ''),
    phone: typeof raw.phone === 'string' ? raw.phone : undefined,
    statement: String(raw.statement || ''),
    heardFrom:
      typeof raw.heardFrom === 'string' && raw.heardFrom.trim()
        ? raw.heardFrom.trim()
        : undefined,
    resume: parseResume(raw.resume),
    status: (raw.status as JobApplicationStatus) || 'new',
    source: raw.source === 'manual' ? 'manual' : 'website',
    payrollPositionId:
      typeof raw.payrollPositionId === 'string' ? raw.payrollPositionId : null,
    submittedAt: parseDate(raw.submittedAt) || new Date(),
    reviewedAt: parseDate(raw.reviewedAt),
    reviewedBy: typeof raw.reviewedBy === 'string' ? raw.reviewedBy : null,
    internalNotes: typeof raw.internalNotes === 'string' ? raw.internalNotes : undefined,
  };
}

export function subscribeJobPostings(onData: (rows: JobPosting[]) => void): Unsubscribe {
  const q = query(collection(db, JOB_POSTINGS), orderBy('updatedAt', 'desc'));
  return onSnapshot(
    q,
    snap => {
      onData(snap.docs.map(d => parseJobPosting(d.id, d.data() as Record<string, unknown>)));
    },
    err => firestoreSnapshotListenError('hiringFirestore', JOB_POSTINGS, err),
  );
}

export function subscribeJobApplications(
  filters: { jobPostingId?: string; status?: JobApplicationStatus },
  onData: (rows: JobApplication[]) => void,
): Unsubscribe {
  let q = query(collection(db, JOB_APPLICATIONS), orderBy('submittedAt', 'desc'));
  if (filters.jobPostingId) {
    q = query(
      collection(db, JOB_APPLICATIONS),
      where('jobPostingId', '==', filters.jobPostingId),
      orderBy('submittedAt', 'desc'),
    );
  } else if (filters.status) {
    q = query(
      collection(db, JOB_APPLICATIONS),
      where('status', '==', filters.status),
      orderBy('submittedAt', 'desc'),
    );
  }
  return onSnapshot(
    q,
    snap => {
      onData(snap.docs.map(d => parseJobApplication(d.id, d.data() as Record<string, unknown>)));
    },
    err => firestoreSnapshotListenError('hiringFirestore', JOB_APPLICATIONS, err),
  );
}

export async function getJobPosting(id: string): Promise<JobPosting | null> {
  const snap = await getDoc(doc(db, JOB_POSTINGS, id));
  if (!snap.exists()) return null;
  return parseJobPosting(snap.id, snap.data() as Record<string, unknown>);
}

export async function getJobPostingByPayrollPositionId(
  payrollPositionId: string,
): Promise<JobPosting | null> {
  const q = query(
    collection(db, JOB_POSTINGS),
    where('payrollPositionId', '==', payrollPositionId),
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return parseJobPosting(d.id, d.data() as Record<string, unknown>);
}

export type JobPostingInput = {
  title: string;
  department: PayrollDepartment | string;
  location: string;
  employmentType: JobEmploymentType;
  description: string;
  requirements: string;
  status: JobPostingStatus;
  payrollPositionId?: string | null;
  slug?: string;
};

export async function createJobPosting(input: JobPostingInput): Promise<string> {
  const uid = auth.currentUser?.uid || '';
  const baseSlug = slugifyJobTitle(input.slug?.trim() || input.title);
  const slug = await ensureUniqueJobSlug(baseSlug);
  const now = serverTimestamp();
  const ref = await addDoc(collection(db, JOB_POSTINGS), {
    slug,
    title: input.title.trim(),
    department: input.department,
    location: input.location.trim(),
    employmentType: input.employmentType,
    description: input.description,
    requirements: input.requirements,
    status: input.status,
    payrollPositionId: input.payrollPositionId || null,
    publishedAt: input.status === 'published' ? now : null,
    closedAt: null,
    applicationCount: 0,
    createdAt: now,
    updatedAt: now,
    createdBy: uid,
  });
  return ref.id;
}

export async function updateJobPosting(
  id: string,
  patch: Partial<JobPostingInput> & { status?: JobPostingStatus },
): Promise<void> {
  const existing = await getJobPosting(id);
  if (!existing) throw new Error('Job posting not found');

  const payload: Record<string, unknown> = {
    updatedAt: serverTimestamp(),
  };
  if (patch.title !== undefined) payload.title = patch.title.trim();
  if (patch.department !== undefined) payload.department = patch.department;
  if (patch.location !== undefined) payload.location = patch.location.trim();
  if (patch.employmentType !== undefined) payload.employmentType = patch.employmentType;
  if (patch.description !== undefined) payload.description = patch.description;
  if (patch.requirements !== undefined) payload.requirements = patch.requirements;
  if (patch.payrollPositionId !== undefined) {
    payload.payrollPositionId = patch.payrollPositionId || null;
  }
  if (patch.slug !== undefined && patch.slug.trim()) {
    payload.slug = await ensureUniqueJobSlug(slugifyJobTitle(patch.slug), id);
  }
  if (patch.status !== undefined) {
    payload.status = patch.status;
    if (patch.status === 'published' && existing.status !== 'published') {
      payload.publishedAt = serverTimestamp();
      payload.closedAt = null;
    }
    if (patch.status === 'closed') {
      payload.closedAt = serverTimestamp();
    }
    if (patch.status === 'draft') {
      payload.publishedAt = null;
      payload.closedAt = null;
    }
  }

  await updateDoc(doc(db, JOB_POSTINGS, id), payload as DocumentData);
}

export async function updateJobApplication(
  id: string,
  patch: {
    status?: JobApplicationStatus;
    internalNotes?: string;
    reviewedBy?: string;
  },
): Promise<void> {
  const payload: Record<string, unknown> = {};
  if (patch.status !== undefined) {
    payload.status = patch.status;
    payload.reviewedAt = serverTimestamp();
    if (patch.reviewedBy) payload.reviewedBy = patch.reviewedBy;
  }
  if (patch.internalNotes !== undefined) payload.internalNotes = patch.internalNotes;
  await updateDoc(doc(db, JOB_APPLICATIONS, id), payload as DocumentData);
}

export async function linkJobPostingToPayrollPosition(
  postingId: string,
  payrollPositionId: string | null,
): Promise<void> {
  await updateDoc(doc(db, JOB_POSTINGS, postingId), {
    payrollPositionId: payrollPositionId || null,
    updatedAt: serverTimestamp(),
  });
}

export type ManualJobApplicationInput = {
  posting: JobPosting;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  statement?: string;
  resume: File;
};

export async function createManualJobApplication(input: ManualJobApplicationInput): Promise<string> {
  const firstName = input.firstName.trim();
  const lastName = input.lastName.trim();
  if (!input.posting?.id) throw new Error('Select a job posting');
  if (!firstName || !lastName) throw new Error('First and last name are required');
  if (!input.resume) throw new Error('Resume is required');
  if (!isAllowedResumeFile(input.resume)) {
    throw new Error('Resume must be a PDF, DOC, or DOCX under 25 MB');
  }

  const uid = auth.currentUser?.uid || '';
  const fileName = sanitizeResumeFileName(input.resume.name);
  const contentType = input.resume.type || 'application/octet-stream';
  const now = serverTimestamp();

  const refDoc = await addDoc(collection(db, JOB_APPLICATIONS), {
    jobPostingId: input.posting.id,
    jobSlug: input.posting.slug,
    jobTitle: input.posting.title,
    firstName,
    lastName,
    email: (input.email || '').trim().toLowerCase(),
    phone: (input.phone || '').trim() || null,
    statement: (input.statement || '').trim(),
    resume: {
      fileName,
      storagePath: '',
      contentType,
      sizeBytes: 0,
    },
    status: 'new',
    source: 'manual',
    payrollPositionId: input.posting.payrollPositionId || null,
    submittedAt: now,
    reviewedAt: null,
    reviewedBy: uid || null,
    internalNotes: '',
    createdBy: uid,
  });

  const storagePath = `${JOB_APPLICATIONS_STORAGE_PREFIX}/${refDoc.id}/${fileName}`;
  try {
    await uploadBytes(ref(storage, storagePath), input.resume, { contentType });
    await updateDoc(refDoc, {
      resume: {
        fileName,
        storagePath,
        contentType,
        sizeBytes: input.resume.size,
      },
    } as DocumentData);
  } catch (err) {
    throw new Error(
      `Candidate saved, but the resume did not upload: ${(err as Error).message || 'storage error'}`,
    );
  }

  await updateDoc(doc(db, JOB_POSTINGS, input.posting.id), {
    applicationCount: increment(1),
    updatedAt: serverTimestamp(),
  } as DocumentData);

  return refDoc.id;
}
